import { useEffect, useState } from 'react';
import '../../../shared/styles/WelcomeTutorial.css';

/* ─── Callout: arrow stem + tip above a hint box ────────────────── */
function Callout({ blink, tall, posClass, children }) {
  return (
    <div className={`wt_callout_wrap ${posClass}`}>
      <div className={`wt_callout_arrow${tall ? ' wt_callout_arrow_tall' : ''}`} />
      <div className={`wt_callout_box${blink ? ' wt_callout_box_blink' : ''}`}>
        {children}
      </div>
    </div>
  );
}

function MagicCircle() {
  return (
    <div className='wt_magic_zone'>
      <div className='wt_magic_circle'>
        <div className='wt_magic_inner'>
          <span className='wt_magic_star'>✦</span>
          <span className='wt_magic_top_text'>Here be the</span>
          <span className='wt_magic_main_text'>Magic</span>
          <span className='wt_magic_star'>✦</span>
        </div>
      </div>
    </div>
  );
}

/* ── Desktop Tutorial ───────────────────────────────────────────── */
function DesktopTutorial({ isDark }) {
  return (
    <div className={`wt_root${isDark ? ' wt_root_dark' : ''}`}>
      {/* ── Hints zone: absolutely positioned callouts under each topbar button ── */}
      <div className='wt_hints_zone'>

        {/* Add Scene: aligned under the + / Scenes button (far left) */}
        <Callout blink posClass='wt_cp_add_scene'>
          <span className='wt_step_badge'>👆 First, do this!</span>
          <strong>Add a Scene</strong>
          <p>
            Hit <strong>+</strong> to conjure your first scene. The whole
            adventure starts right here!
          </p>
        </Callout>

        {/* Open Display: row 1, aligned under the Fullscreen button */}
        <Callout posClass='wt_cp_display'>
          <strong>🖥️ Open the Screen</strong>
          <p>
            Launch the player window, pop it on a second monitor & smash F11.
            <em> That's</em> what your players see!
          </p>
        </Callout>

        {/* Asset Management: row 2 (staggered to avoid overlap with Open Display) */}
        <Callout tall posClass='wt_cp_asset'>
          <strong>🗂️ Asset Management</strong>
          <p>
            Upload images and media to populate your scenes with custom tokens,
            maps, and artwork.
          </p>
        </Callout>

        {/* Take a Break: row 1, aligned under the Armchair button */}
        <Callout posClass='wt_cp_break'>
          <strong>☕ Take a Break!</strong>
          <p>
            Step away — your players see a cozy intermission while you
            strategize (or snack).
          </p>
        </Callout>

      </div>

      {/* ── Main body: mock sidebar rows with inline annotations ── */}
      <div className='wt_main_layout'>
        {/* Left: mock sidebar + right: magic circle */}
        <div className='wt_sidebar_plus_annotations'>

          {/* Row 1: Session token */}
          <div className='wt_anno_row'>
            <div className='wt_mock_session'>
              <span>PLAYER SESSION</span>
              <span className='wt_mock_token_dots'>•••••••••</span>
            </div>
            <div className='wt_anno_connector' />
            <div className='wt_anno_bubble'>
              <strong>🔑 Your Session Token</strong>
              <p>
                Share this code with your players — they type it in to join
                your game and follow along in real time!
              </p>
            </div>
          </div>

          {/* Row 2: Tools grid */}
          <div className='wt_anno_row'>
            <div className='wt_mock_tools'>
              {Array(15).fill(null).map((_, i) => (
                <div key={`tool-${i}`} className='wt_mock_tool_btn' />
              ))}
            </div>
            <div className='wt_anno_connector' />
            <div className='wt_anno_bubble'>
              <strong>⚔️ Orchestrate Your Game</strong>
              <p>
                Pick a TTRPG system, craft your world, create characters,
                roll dice, and bend the scene to your will. Every button is a
                rabbit hole worth exploring!
              </p>
            </div>
          </div>

          {/* Row 3: World elements (tokens/effects/layers) */}
          <div className='wt_anno_row wt_anno_row_world'>
            <div className='wt_mock_world_sections'>
              <div className='wt_mock_section_hdr'>TOKENS</div>
              <div className='wt_mock_empty_row'>No tokens yet</div>
              <div className='wt_mock_section_hdr'>BATTLE EFFECTS</div>
              <div className='wt_mock_empty_row'>No battle effects yet</div>
              <div className='wt_mock_section_hdr'>LAYERS</div>
              <div className='wt_mock_empty_row'>No layers yet</div>
            </div>
            <div className='wt_anno_connector' />
            <div className='wt_anno_bubble'>
              <strong>🌍 Your Living World</strong>
              <p>
                Tokens, Battle Effects, Layers — the building blocks of your
                scene live here. As you craft your world, this panel comes
                alive with possibility!
              </p>
            </div>
          </div>

        </div>

        {/* Right: Magic circle */}
        <MagicCircle />
      </div>
    </div>
  );
}

/* ── Mobile Tutorial ────────────────────────────────────────────── */
function MobileTutorial({ isDark }) {
  return (
    <div className={`wt_root wt_mobile${isDark ? ' wt_root_dark' : ''}`}>
      <div className='wt_mobile_hint wt_mobile_hint_blink'>
        <span className='wt_step_badge'>👆 Start Here!</span>
        <strong>Create Your First Scene</strong>
        <p>
          Tap the <strong>+</strong> button at the top to create a scene and
          kick off your adventure. This is step one!
        </p>
      </div>

      <div className='wt_mobile_hint'>
        <strong>🗂️ Manage Your Scenes</strong>
        <p>
          Use the Scenes button in the top bar to switch between scenes, add
          new ones, or dive back into an old encounter.
        </p>
      </div>

      <div className='wt_mobile_magic'>
        <MagicCircle />
      </div>

      <div className='wt_mobile_hint'>
        <strong>🔑 Your Session Token</strong>
        <p>
          Once a scene is active, share the session token with your players so
          they can join and see everything in real time!
        </p>
      </div>

      <div className='wt_mobile_hint'>
        <strong>⚔️ Command Central</strong>
        <p>
          Choose your TTRPG system, build characters, roll dice, and
          orchestrate every dramatic moment. Take your time to explore each
          tool — they're all there for you!
        </p>
      </div>

      <div className='wt_mobile_hint'>
        <strong>🌍 Tokens · Battle Effects · Layers</strong>
        <p>
          The elements of your world appear in the sidebar. Add tokens, paint
          effects, and stack layers to bring your scene to life!
        </p>
      </div>

      <div className='wt_mobile_hint'>
        <strong>🖥️ The Player Screen</strong>
        <p>
          Open the display and put it on a big screen or second monitor. Hit
          F11 for the full immersive experience. That's what your players see!
        </p>
      </div>

      <div className='wt_mobile_hint'>
        <strong>☕ Need a Minute?</strong>
        <p>
          Tap the break button to show a friendly intermission to your
          players. Step away, grab a snack, and plot your next move. 😄
        </p>
      </div>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────────────── */
export default function WelcomeTutorial({ mode = 'light' }) {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isDark = mode === 'dark';
  const isMobile = windowWidth < 775;

  if (isMobile) {
    return <MobileTutorial isDark={isDark} />;
  }

  return <DesktopTutorial isDark={isDark} />;
}
